import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { ApiError, NetworkError, ValidationError, AuthenticationError } from '@/lib/api-errors';
import { sanitizeEmail } from '@/lib/sanitize';
import { RateLimiters } from '@/lib/rate-limiter';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
// Checkbox removed — rememberEmail feature deferred
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { logger } from "@/utils/logger";
import { supabase } from "@/integrations/supabase/client";

export default function Login() {
  logger.component('Login', 'Component rendering started');
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    signIn,
    user,
    loading: authLoading
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleInputChange = (field: string, value: string | boolean) => {
    // Sanitize email input for security
    if (field === 'email' && typeof value === 'string') {
      value = sanitizeEmail(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Rate limit login attempts (5 per minute)
    const rateCheck = RateLimiters.auth.check();
    if (!rateCheck.allowed) {
      toast({
        title: "Too many attempts",
        description: `Please wait ${Math.ceil((rateCheck.retryAfter || 0) / 1000)} seconds before trying again.`,
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    RateLimiters.auth.record(); // Record this attempt
    
    try {
      const {
        error
      } = await signIn(formData.email, formData.password);
      if (error) {
        if (error.message.includes('Email not confirmed')) {
          toast({
            title: "Email not verified",
            description: "Please check your email and verify your account before signing in.",
            variant: "destructive"
          });
          navigate(`/auth/verify-email?email=${encodeURIComponent(formData.email)}`);
        } else {
          toast({
            title: "Sign in failed",
            description: error.message,
            variant: "destructive"
          });
        }
      } else {
        // Re-fetch session to get latest user data including email_confirmed_at
        const { data: { session: freshSession } } = await supabase.auth.getSession();
        const currentUser = freshSession?.user;

        // Check email verification status
        if (currentUser && !currentUser.email_confirmed_at) {
          toast({
            title: "Email not verified",
            description: "Please verify your email before accessing the dashboard.",
            variant: "destructive"
          });
          navigate(`/auth/verify-email?email=${encodeURIComponent(formData.email)}`);
          return;
        }

        // Check if profile is complete (has a role assigned)
        if (currentUser) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('role')
            .eq('user_id', currentUser.id)
            .single();

          if (!profile?.role) {
            toast({
              title: "Welcome!",
              description: "Let's complete your profile setup."
            });
            navigate('/auth/complete-profile', { replace: true });
            return;
          }
        }

        toast({
          title: "Welcome back!",
          description: "You have successfully signed in."
        });

        // Redirect to intended page or dashboard
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, {
          replace: true
        });
      }
    } catch (error) {
      if (error instanceof AuthenticationError) {
        toast({
          title: "Login Failed",
          description: "Invalid email or password. Please try again.",
          variant: "destructive"
        });
        Sentry.captureException(error, {
          tags: { context: 'auth_error', operation: 'login', component: 'Login' },
          extra: { message: error.message }
        });
      } else if (error instanceof NetworkError) {
        toast({
          title: "Connection Error",
          description: "Unable to connect. Check your internet connection and try again.",
          variant: "destructive"
        });
        Sentry.captureException(error, {
          tags: { context: 'network_error', operation: 'login' },
          extra: { message: error.message }
        });
      } else if (error instanceof ValidationError) {
        toast({
          title: "Validation Error",
          description: "Please enter valid email and password.",
          variant: "destructive"
        });
        Sentry.captureException(error, {
          tags: { context: 'validation_error', operation: 'login' },
          extra: { message: error.message }
        });
      } else {
        toast({
          title: "Login Failed",
          description: "Something went wrong. Please try again.",
          variant: "destructive"
        });
        Sentry.captureException(error, {
          tags: { context: 'unknown_error', operation: 'login' },
          extra: { message: 'Unknown login error' }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Add error boundary fallback for debugging
  if (!signIn) {
    Sentry.captureException(new Error('[Login] signIn function not available from auth context'), {
      tags: { context: 'auth_error', operation: 'login', component: 'Login' },
      extra: { message: 'signIn function not available from auth context' }
    });
    return <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <Card className="shadow-lg">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-destructive mb-4">Authentication Error</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Unable to load authentication system. Please try refreshing the page.
              </p>
              
              <Button onClick={() => window.location.reload()} className="w-full mt-4">
                Refresh Page
              </Button>
            </CardContent>
          </Card>
        </div>
        </div>;
  }
  
  logger.component('Login', 'Rendering main login form');
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <Card className="shadow-lg">
          <CardHeader className="text-left pb-6">
            <CardTitle className="text-foreground font-semibold text-2xl">Sign in to your account</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-foreground">
                  Email
                </Label>
                <Input id="email" type="email" value={formData.email} onChange={e => handleInputChange("email", e.target.value)} className="mt-2" required />
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-medium text-foreground">
                  Password
                </Label>
                <div className="relative mt-2">
                  <Input id="password" type={showPassword ? "text" : "password"} value={formData.password} onChange={e => handleInputChange("password", e.target.value)} className={formData.password.length > 0 ? "pr-10" : ""} required />
                  {formData.password.length > 0 && <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                    </button>}
                </div>
              </div>

              <div className="flex items-center justify-end">
                <Link to="/auth/forgot-password" className="text-sm text-primary hover:underline">
                  Forgot password?
                </Link>
              </div>

              <div className="pt-4 py-[20px]">
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" disabled={loading}>
                  {loading ? "Signing in..." : "Sign in"}
                </Button>
              </div>

              {/* TODO: OAuth Google — Phase 2 */}

              <div className="text-center pt-4">
                <p className="text-sm text-muted-foreground">
                  New to mySuccession?{" "}
                  <Link to="/register" className="text-primary hover:underline font-medium">
                    Create account
                  </Link>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
        
        {/* Security Notice */}
        <div className="mt-6 flex items-start gap-2 text-sm text-muted-foreground">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-xs">
            On a shared computer, make sure to sign out when you're done. This helps keep your account secure from other people using your device.
          </p>
        </div>
      </div>
    </div>
  );
}